import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import SimpleBar from "simplebar-react";
import { Nav, Tooltip, Collapse, Popover, PopoverBody } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";

import dashboard from "assets/icons/dashboard.svg";
import partners from "assets/icons/partners.svg";
import transactions from "assets/icons/transactions.svg";
import products from "assets/icons/products.svg";
import campaigns from "assets/icons/campaigns.svg";
import commission from "assets/icons/commission.svg";
import content from "assets/icons/content.svg";
import admins from "assets/icons/admin-users.svg";
import profile from "assets/icons/profile.svg";
import help from "assets/icons/help.svg";
import manual from "assets/icons/manual.svg";
import signout from "assets/icons/signout.svg";
import subMenu from "assets/icons/sub-menu.svg";

const SidebarContent = ({ condensed = false }) => {
  const { onLogout } = useAuth();

  const handleLogout = async (e) => {
    e.preventDefault();
    await onLogout();
  };
  const location = useLocation();
  const [tooltips, setTooltips] = useState({});
  const [submenuOpen, setSubmenuOpen] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(null);
  const [, forceRender] = useState({});

  useEffect(() => {
    // Force re-render when URL parameters change
    forceRender({});
  }, [location.search]);

  // Toggle function for tooltips
  const toggleTooltip = (id) => {
    if (!condensed) return;
    setTooltips((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const toggleSubmenu = (idx) => {
    setSubmenuOpen(submenuOpen === idx ? null : idx);
  };

  const togglePopover = (idx) => {
    setPopoverOpen(popoverOpen === idx ? null : idx);
  };

  const isActive = (path) => {
    const fullPath = location.pathname + location.search;
    if (path === "/") {
      return fullPath === path;
    } else {
      return fullPath.startsWith(path);
    }
  };

  const isSubItemsActive = (submenu) => {
    const fullPath = location.pathname + location.search;
    if (fullPath === "/") return false;
    const activeSubmenu = submenu.find((item) => fullPath === item.link);
    if (activeSubmenu) return true;
    return submenu[0].link.startsWith(location.pathname);
  };

  const menuItems = [
    {
      icon: dashboard,
      label: "หน้าหลักแอดมิน",
      link: "/",
    },
    {
      icon: partners,
      label: "จัดการพาร์ทเนอร์",
      link: "/partners",
    },
    {
      icon: transactions,
      label: "รายการธุรกรรม",
      link: "/transactions",
    },
    {
      icon: products,
      label: "จัดการสินค้า",
      link: "/products",
    },
    {
      icon: commission,
      label: "จัดการค่าคอมมิชชัน",
      link: "/commission",
    },
    {
      icon: campaigns,
      label: "จัดการแคมเปญ",
      link: "/campaigns",
    },
    {
      icon: content,
      label: "จัดการเนื้อหาบนเว็บ",
      link: "/contents",
    },
    {
      icon: admins,
      label: "จัดการแอดมินผู้ใช้งาน",
      link: "/admin",
    },
    {
      divider: true,
    },
    {
      icon: profile,
      label: "โปรไฟล์ของฉัน",
      link: "/profile",
      subItems: [
        {
          label: "ข้อมูลส่วนตัว",
          link: "/profile?tab=profile",
        },
        // {
        //   label: "ประวัติการใช้งาน",
        //   link: "/profile?tab=activity",
        // },
      ],
    },
    {
      icon: help,
      label: "ฝ่ายช่วยเหลือ",
      link: "/help",
    },
    {
      icon: manual,
      label: "คู่มือการใช้งาน",
      link: "/manual",
    },
    {
      divider: true,
    },
    {
      icon: signout,
      label: "ออกจากระบบ",
      link: "#",
      onClick: handleLogout,
    },
  ];

  return (
    <SimpleBar>
      <div id="sidebar-menu">
        <Nav vertical className={`sidebar-nav ${condensed ? "condensed" : ""}`}>
          {menuItems.map((item, idx) => {
            if (item.divider) {
              return <hr key={idx} className="sidebar-divider" />;
            }

            if (item.subItems) {
              return (
                <div key={idx} className="nav-item">
                  <Link
                    className={`navs-link ${isSubItemsActive(item.subItems) ? "active" : ""}`}
                    to="#"
                    id={(condensed ? "" : "no") + `tooltip-${idx}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (condensed) {
                        togglePopover(idx);
                      } else {
                        toggleSubmenu(idx);
                      }
                    }}
                  >
                    <img src={item.icon} className="nav-icon" alt={item.label} />
                    {!condensed && (
                      <>
                        <span className="nav-text">{item.label}</span>
                        <i className={`fa fa-angle-down menu-arrow ${submenuOpen === idx ? "rotate" : ""}`} />
                      </>
                    )}
                  </Link>

                  {condensed && popoverOpen !== idx && (
                    <Tooltip placement="right" isOpen={tooltips[`tooltip-${idx}`]} target={`tooltip-${idx}`} toggle={() => toggleTooltip(`tooltip-${idx}`)} transition={{ timeout: 200 }}>
                      {item.label}
                    </Tooltip>
                  )}

                  {/* Popover for condensed mode */}
                  {condensed && (
                    <Popover placement="right" isOpen={popoverOpen === idx} target={(condensed ? "" : "no") + `tooltip-${idx}`} toggle={() => togglePopover(idx)} className="close-menu" trigger="legacy" delay={{ show: 0, hide: 0 }} transition={{ timeout: 200 }}>
                      <PopoverBody className="p-0">
                        <Nav className="sub-nav">
                          {item.subItems.map((subItem, subIdx) => (
                            <Link key={subIdx} className={`navs-link ${isActive(subItem.link) ? "active" : ""}`} to={subItem.link} onClick={() => togglePopover(idx)}>
                              <img src={subMenu} className="nav-icon" alt="" />
                              <span className="nav-text">{subItem.label}</span>
                            </Link>
                          ))}
                        </Nav>
                      </PopoverBody>
                    </Popover>
                  )}

                  {/* Normal submenu for non-condensed mode */}
                  {!condensed && (
                    <Collapse isOpen={submenuOpen === idx}>
                      <Nav className="sub-nav">
                        {item.subItems.map((subItem, subIdx) => (
                          <Link key={subIdx} className={`navs-link ${isActive(subItem.link) ? "active" : ""}`} to={subItem.link}>
                            <img src={subMenu} className="nav-icon" alt="" />
                            <span className="nav-text">{subItem.label}</span>
                          </Link>
                        ))}
                      </Nav>
                    </Collapse>
                  )}
                </div>
              );
            }

            return (
              <React.Fragment key={idx}>
                {item.link ? (
                  <Link to={item.link} className={`navs-link ${isActive(item.link) ? "active" : ""}`} id={condensed ? `tooltip-${idx}` : null} onClick={item.onClick || null}>
                    <img src={item.icon} className="nav-icon" alt={item.label} />
                    {!condensed && <span className="nav-text">{item.label}</span>}
                  </Link>
                ) : (
                  <span className={`navs-link ${isActive(item.link) ? "active" : ""}`} id={condensed ? `tooltip-${idx}` : null} onClick={item.onClick || null}>
                    <img src={item.icon} className="nav-icon" alt={item.label} />
                    {!condensed && <span className="nav-text">{item.label}</span>}
                  </span>
                )}
                {condensed && item.label && (
                  <Tooltip placement="right" isOpen={tooltips[`tooltip-${idx}`]} target={`tooltip-${idx}`} toggle={() => toggleTooltip(`tooltip-${idx}`)} transition={{ timeout: 200 }}>
                    {item.label}
                  </Tooltip>
                )}
              </React.Fragment>
            );
          })}
        </Nav>
      </div>
    </SimpleBar>
  );
};

SidebarContent.propTypes = {
  condensed: PropTypes.bool,
};

export default SidebarContent;
